import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
  useMemo,
} from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type {
  IItemData,
  IItemDataResponse,
  IPurchaseRequestItemBody,
  ISuppliars,
} from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';
import { getDiscountStatusFn } from '@/pages/PurchasesContainer/RequestPerHand/utils/DiscountCheck';
import { calculatePricing } from '@/pages/PurchasesContainer/RequestPerHand/utils/priceCalculations';

import { DiscountBatchSection } from './components/DiscountBatchSection';
import DuplicateItemDialog from './components/DuplicateItemDialog';
import { ItemSelectionSection } from './components/ItemSelectionSection';
import { ItemsPriceCard } from './components/ItemsPriceCard';
import { LastIncomingSection } from './components/LastIncomingSection';
import { QtyPriceSection } from './components/QtyPriceSection';
import { SummarySection } from './components/SummarySection';
import { useFormHandlers } from './hooks/useFormHandlers';
import { useItemDataMutation } from './hooks/useItemDataMutation';

type Props = {
  items: IItemData[] | undefined;
  suppliar: ISuppliars | null;
  addRequestItem?: (newItem: IPurchaseRequestItemBody) => boolean;
  updateRequestItem?: (newItem: IPurchaseRequestItemBody) => boolean;
  requestItems: IPurchaseRequestItemBody[];
  selectedUpdateItem: IPurchaseRequestItemBody | null;
};

// Safe parsers to avoid NaN - moved outside component to prevent recreation
const safeParseFloat = (value: string): number => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const safeParseInt = (value: string): number => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to safely convert numbers to strings
const safeToString = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return value.toString();
};

// Memoized toast components for better performance
const successToast = (message: string) => toast.success(message);
const errorToast = (message: string) => toast.error(message);

// Pre-defined memoized UI elements
const NoDataMessage = memo(() => (
  <div className="mt-2 flex items-center gap-2 rounded-md bg-yellow-50 p-2 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300">
    <span className="text-sm">برجاء اختيار صنف 🔍 ✨</span>
  </div>
));
NoDataMessage.displayName = 'NoDataMessage';

const RequestInputs = memo(
  ({
    items,
    suppliar,
    addRequestItem,
    updateRequestItem,
    requestItems,
    selectedUpdateItem,
  }: Props) => {
    // Core state - use reducers for related state to minimize rerenders
    const [selectedItem, setSelectedItem] = useState<IItemData | null>(null);
    const [itemData, setItemData] = useState<IItemDataResponse | null>(null);
    const [dialogState, setDialogState] = useState({
      showDuplicateDialog: false,
      pendingItem: null as IPurchaseRequestItemBody | null,
      duplicateItem: null as IPurchaseRequestItemBody | null,
    });

    // Destructure dialog state for easier access
    const { showDuplicateDialog, pendingItem, duplicateItem } = dialogState;

    // Refs with explicit typing
    const selectItemRef = useRef<HTMLInputElement | null>(null);
    const confirmButtonRef = useRef<HTMLButtonElement>(null!);
    const prevSuppliarRef = useRef<ISuppliars | null>(null);

    // Extract form handlers from custom hook
    const {
      qtyInput,
      taxInput,
      discountInput,
      specialDiscountInput,
      bonusInput,
      bnoInput,
      expireDate,
      setQtyInput,
      setTaxInput,
      setDiscountInput,
      setSpecialDiscountInput,
      setBonusInput,
      setBnoInput,
      setExpireDate,
      qtyRef,
      taxRef,
      discountRef,
      specialDiscountRef,
      bonusRef,
      bnoRef,
      expireDateRef,
      submitButtonRef,
      handleQtyChange,
      handleQtyBlur,
      handleQtyKeyDown,
      handleTaxChange,
      handleTaxBlur,
      handleTaxKeyDown,
      handleDiscountChange,
      handleDiscountBlur,
      handleDiscountKeyDown,
      handleSpecialDiscountChange,
      handleSpecialDiscountBlur,
      handleSpecialDiscountKeyDown,
      handleBonusChange,
      handleBonusBlur,
      handleBonusKeyDown,
      handleBnoChange,
      handleBnoBlur,
      handleBnoKeyDown,
      handleExpireDateChange,
      handleExpireDateBlur,
      handleExpireDateKeyDown,
      handleFocusSelect,
      fnToGoToQty,
      resetForm,
    } = useFormHandlers();

    // Data fetching with optimized mutation hook
    const { fetchItemData, isPending } = useItemDataMutation({
      setBnoInput,
      setDiscountInput,
      setExpireDate,
      setItemData,
    });

    // Reset form with optimized callback
    const resetFormCompletely = useCallback(() => {
      setSelectedItem(null);
      resetForm();
      setItemData(null);
      if (selectItemRef.current) {
        selectItemRef.current.focus();
      }
    }, [resetForm]);

    // Custom expire date focus handler
    const handleCustomExpireDateFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (itemData?.itemLastIncoming?.ExpireDate) {
          const expireDate = new Date(itemData.itemLastIncoming.ExpireDate);
          const month = (expireDate.getMonth() + 1).toString().padStart(2, '0');
          const year = expireDate.getFullYear();
          setExpireDate(`${month}/${year}`);
        }
        handleFocusSelect(e);
      },
      [
        itemData?.itemLastIncoming?.ExpireDate,
        setExpireDate,
        handleFocusSelect,
      ],
    );

    // Optimized item selection handler
    const handleSelectItem = useCallback(
      async (item: IItemData | null) => {
        setSelectedItem(item);

        if (!item) return;

        if (!suppliar?.ID) {
          errorToast('يجب اختيار المورد');
          return;
        }

        await fetchItemData({
          itemId: item.ID,
          supplierId: suppliar.ID,
        });

        // Move to quantity field after data is fetched
        requestAnimationFrame(fnToGoToQty);
      },
      [suppliar?.ID, fetchItemData, fnToGoToQty],
    );

    // Dialog handlers with performance optimizations
    const handleConfirmUpdate = useCallback(() => {
      if (!pendingItem) return;

      const handler = updateRequestItem || addRequestItem;
      if (!handler) return;

      const success = handler(pendingItem);

      if (success) {
        successToast('تم تعديل العنصر بنجاح');
        resetFormCompletely();
      } else {
        errorToast('فشل في تحديث العنصر');
      }

      setDialogState((prev) => ({
        ...prev,
        showDuplicateDialog: false,
        pendingItem: null,
      }));
    }, [pendingItem, updateRequestItem, addRequestItem, resetFormCompletely]);

    const handleCancelUpdate = useCallback(() => {
      setDialogState((prev) => ({
        ...prev,
        showDuplicateDialog: false,
        pendingItem: null,
      }));
      resetFormCompletely();
      errorToast('تم إلغاء التعديل');
    }, [resetFormCompletely]);

    // Highly optimized calculation of submission data
    const prepareSubmitData = useCallback(
      (selectedItem: IItemData) => {
        // Calculate pricing once
        const {
          totalTax,
          netDiscount,
          TotalAfterDiscount,
          mainPrice,
          totalCustomerPrice,
        } = calculatePricing({
          price: selectedItem.Price,
          quantity: qtyInput,
          bonus: bonusInput,
          discount: discountInput,
          specialDiscount: specialDiscountInput,
          tax: taxInput,
        });

        const mainItemData = itemData?.AllItemsData?.find(
          (item) => item?.IsMain,
        );
        const discountStatus = getDiscountStatusFn(
          netDiscount,
          itemData?.offers,
        );

        // Create object once without intermediate variables
        return {
          _id: selectedUpdateItem?._id || null,
          TempID:
            selectedUpdateItem?.TempID ||
            `${selectedItem.ID}_${bnoInput}_${expireDate}`,
          Item_ID: selectedItem.ID,
          ItemName: selectedItem.Name,
          Price: selectedItem.Price,
          MainPrice: mainPrice,
          Discount: safeParseFloat(discountInput),
          FinishDiscount: netDiscount,
          Qty: safeParseInt(qtyInput),
          Bonus: safeParseInt(bonusInput),
          Tax: safeParseFloat(taxInput),
          TotalTax: totalTax,
          BNO: bnoInput,
          ExpireDate: expireDate,
          DiscountPro: safeParseFloat(specialDiscountInput),
          DiscountStatus: discountStatus,
          WholeSaleStatus: mainItemData?.WholeSaleStatus || 'noTrans',
          RetailStatus: mainItemData?.RetailStatus || 'noTrans',
          TotalAfterDiscount,
          TotalCustomerPrice: totalCustomerPrice,
          UnderRequest: itemData?.offers?.underRequest || 0,
        };
      },
      [
        selectedUpdateItem,
        itemData,
        qtyInput,
        bonusInput,
        discountInput,
        specialDiscountInput,
        taxInput,
        bnoInput,
        expireDate,
      ],
    );

    // Optimized submit handler with early returns
    const handleSubmit = useCallback(() => {
      if (!selectedItem) return;

      const submitData = prepareSubmitData(selectedItem);

      // Updating existing item
      if (selectedUpdateItem && updateRequestItem) {
        try {
          const updated = updateRequestItem(submitData);
          if (updated) {
            successToast('تم تعديل العنصر بنجاح');
            resetFormCompletely();
          } else {
            errorToast('فشل في تعديل العنصر');
          }
        } catch (error) {
          errorToast('حدث خطأ أثناء تعديل العنصر');
        }
        return;
      }

      // Adding new item
      if (addRequestItem) {
        try {
          const underRequest = itemData?.offers?.underRequest || 0;
          if (underRequest > 0) {
            errorToast('هذا الصنف عليه طلبات معلقة');
          }

          const added = addRequestItem(submitData);
          if (added) {
            successToast('تم إضافة العنصر بنجاح');
            resetFormCompletely();
          } else {
            // Handle duplicate with optimized search
            const duplicateIndex = requestItems.findIndex(
              (item) =>
                (submitData._id !== null && item._id === submitData._id) ||
                item.TempID === submitData.TempID,
            );

            setDialogState({
              showDuplicateDialog: true,
              pendingItem: submitData,
              duplicateItem: requestItems[duplicateIndex],
            });
          }
        } catch (error) {
          errorToast('حدث خطأ أثناء إضافة العنصر');
        }
      }
    }, [
      selectedItem,
      prepareSubmitData,
      selectedUpdateItem,
      updateRequestItem,
      addRequestItem,
      itemData?.offers?.underRequest,
      requestItems,
      resetFormCompletely,
    ]);

    // Form update effect with optimizations
    useEffect(() => {
      if (!selectedUpdateItem) return;

      const itemToUpdate =
        items?.find((item) => item.ID === selectedUpdateItem.Item_ID) || null;
      if (!itemToUpdate) return;

      setSelectedItem(itemToUpdate);

      // Only fetch data if both item and supplier exist
      if (itemToUpdate && suppliar?.ID) {
        fetchItemData({
          itemId: itemToUpdate.ID,
          supplierId: suppliar.ID,
        }).then(() => {
          // Update all form fields in a batch
          const formFields = [
            {
              ref: qtyRef,
              setter: setQtyInput,
              value: safeToString(selectedUpdateItem.Qty),
            },
            {
              ref: taxRef,
              setter: setTaxInput,
              value: safeToString(selectedUpdateItem.Tax),
            },
            {
              ref: discountRef,
              setter: setDiscountInput,
              value: safeToString(selectedUpdateItem.Discount),
            },
            {
              ref: specialDiscountRef,
              setter: setSpecialDiscountInput,
              value: safeToString(selectedUpdateItem.DiscountPro),
            },
            {
              ref: bonusRef,
              setter: setBonusInput,
              value: safeToString(selectedUpdateItem.Bonus),
            },
            {
              ref: bnoRef,
              setter: setBnoInput,
              value: selectedUpdateItem.BNO || '',
            },
            {
              ref: expireDateRef,
              setter: setExpireDate,
              value: selectedUpdateItem.ExpireDate || '',
            },
          ];

          // Batch update refs and state to minimize layout thrashing
          requestAnimationFrame(() => {
            formFields.forEach(({ ref, setter, value }) => {
              if (ref.current) ref.current.value = value;
              setter(value);
            });

            // Focus on quantity field
            if (qtyRef.current) {
              qtyRef.current.focus();
              qtyRef.current.select();
            }
          });
        });

        // Update submit button text
        if (submitButtonRef.current) {
          submitButtonRef.current.textContent = 'تعديل العنصر';
        }
      }
    }, [
      selectedUpdateItem,
      items,
      suppliar,
      fetchItemData,
      setQtyInput,
      setTaxInput,
      setDiscountInput,
      setSpecialDiscountInput,
      setBonusInput,
      setBnoInput,
      setExpireDate,
      qtyRef,
      taxRef,
      discountRef,
      specialDiscountRef,
      bonusRef,
      bnoRef,
      expireDateRef,
      submitButtonRef,
    ]);

    // Focus confirm button in dialog
    useEffect(() => {
      if (showDuplicateDialog && confirmButtonRef.current) {
        requestAnimationFrame(() => confirmButtonRef.current?.focus());
      }
    }, [showDuplicateDialog]);

    // Reset on supplier change with ref comparison for performance
    useEffect(() => {
      if (prevSuppliarRef.current?.ID !== suppliar?.ID) {
        if (!suppliar) {
          setSelectedItem(null);
          resetForm();
          setItemData(null);
        }
        prevSuppliarRef.current = suppliar;
      }
    }, [suppliar, resetForm]);

    // Highly memoized UI components
    const submitButtonInfo = useMemo(
      () => ({
        disabled: !selectedItem || !suppliar,
        className: `
          w-full px-4 py-2.5 rounded-md font-medium shadow-sm
          transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-offset-1 
          disabled:opacity-50 disabled:cursor-not-allowed
          ${
            selectedUpdateItem
              ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600'
              : 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-600'
          }
        `,
        text: selectedUpdateItem ? 'تعديل الصنف' : 'إضافة الصنف',
      }),
      [selectedItem, suppliar, selectedUpdateItem],
    );

    // Memoized sections with proper dependency arrays
    const ItemsSection = useMemo(() => {
      if (!itemData?.AllItemsData?.length || !selectedItem) return null;
      return <ItemsPriceCard items={itemData.AllItemsData} />;
    }, [itemData?.AllItemsData, selectedItem]);

    const FormSection = useMemo(
      () =>
        itemData ? (
          <div className="grid grid-cols-1 gap-2">
            <LastIncomingSection itemData={itemData} />
          </div>
        ) : null,
      [itemData],
    );

    const ItemDataContent = useMemo(() => {
      if (!itemData?.AllItemsData && !itemData?.offers) {
        return <NoDataMessage />;
      }

      return <div className="grid grid-cols-1 gap-2 p-4">{ItemsSection}</div>;
    }, [itemData, ItemsSection]);

    // Memoized handlers for child components
    const handleDialogOpenChange = useCallback((open: boolean) => {
      setDialogState((prev) => ({
        ...prev,
        showDuplicateDialog: open,
      }));
    }, []);

    // Render with minimal inline calculations
    return (
      <div className="rounded-md border">
        <ItemSelectionSection
          items={items}
          selectedItem={selectedItem}
          itemData={itemData}
          suppliar={suppliar}
          selectItemRef={selectItemRef}
          isPending={isPending}
          handleSelectItem={handleSelectItem}
          fnToGoToQty={fnToGoToQty}
        />

        <ScrollArea className="grid h-[calc(100vh-30rem)] grid-cols-1 gap-2">
          <div className="p-4">
            <div className="grid grid-cols-1 gap-2">
              <QtyPriceSection
                selectedItem={selectedItem}
                suppliar={!!suppliar}
                qtyInput={qtyInput}
                qtyRef={qtyRef}
                handleQtyChange={handleQtyChange}
                handleQtyBlur={handleQtyBlur}
                handleFocusSelect={handleFocusSelect}
                handleQtyKeyDown={handleQtyKeyDown}
                isPending={isPending}
              />
            </div>

            <DiscountBatchSection
              selectedItem={selectedItem}
              suppliar={!!suppliar}
              qtyInput={qtyInput}
              taxInput={taxInput}
              discountInput={discountInput}
              specialDiscountInput={specialDiscountInput}
              bonusInput={bonusInput}
              bnoInput={bnoInput}
              expireDate={expireDate}
              discountRef={discountRef}
              taxRef={taxRef}
              specialDiscountRef={specialDiscountRef}
              bonusRef={bonusRef}
              bnoRef={bnoRef}
              expireDateRef={expireDateRef}
              setDiscountInput={setDiscountInput}
              handleDiscountChange={handleDiscountChange}
              handleTaxChange={handleTaxChange}
              handleSpecialDiscountChange={handleSpecialDiscountChange}
              handleDiscountBlur={handleDiscountBlur}
              handleTaxBlur={handleTaxBlur}
              handleSpecialDiscountBlur={handleSpecialDiscountBlur}
              handleFocusSelect={handleFocusSelect}
              handleDiscountKeyDown={handleDiscountKeyDown}
              handleTaxKeyDown={handleTaxKeyDown}
              handleSpecialDiscountKeyDown={handleSpecialDiscountKeyDown}
              handleBonusChange={handleBonusChange}
              handleBonusBlur={handleBonusBlur}
              handleBonusKeyDown={handleBonusKeyDown}
              handleBnoChange={handleBnoChange}
              handleBnoBlur={handleBnoBlur}
              handleBnoKeyDown={handleBnoKeyDown}
              handleExpireDateChange={handleExpireDateChange}
              handleExpireDateBlur={handleExpireDateBlur}
              handleExpireDateFocus={handleCustomExpireDateFocus}
              handleExpireDateKeyDown={handleExpireDateKeyDown}
              isPending={isPending}
            />

            <SummarySection
              selectedItem={selectedItem}
              qtyInput={qtyInput}
              taxInput={taxInput}
              discountInput={discountInput}
              specialDiscountInput={specialDiscountInput}
              bonusInput={bonusInput}
              itemData={itemData}
            />

            {FormSection}
          </div>

          <div className="grid grid-cols-1 gap-1 p-4">{ItemDataContent}</div>

          {/* Duplicate Item Dialog - only render when needed */}
          {showDuplicateDialog && (
            <DuplicateItemDialog
              isOpen={showDuplicateDialog}
              onOpenChange={handleDialogOpenChange}
              item={pendingItem}
              duplicateItem={duplicateItem}
              onConfirm={handleConfirmUpdate}
              onCancel={handleCancelUpdate}
              confirmButtonRef={confirmButtonRef}
            />
          )}
        </ScrollArea>
        <div className="mt-2 flex justify-end border-t p-4">
          <Button
            ref={submitButtonRef}
            onClick={handleSubmit}
            disabled={submitButtonInfo.disabled}
            className={submitButtonInfo.className}
          >
            {submitButtonInfo.text}
          </Button>
        </div>
      </div>
    );
  },
);

RequestInputs.displayName = 'RequestInputs';

export default RequestInputs;
